import { Icons } from "assets/icons";
import AddEditLoader from "components/common/detailsPage/AddEditLoader";
import FileUpload from "components/common/FileUpload";
import MultiSelect from "components/common/MultiSelect";
import NoPermission from "components/common/NoPermission";
import PageHeader from "components/common/PageHeader";
import SingleSelect from "components/common/SingleSelect";
import Switch from "components/common/Switch";
import CustomColorField from "components/form/CustomColorField";
import CustomField from "components/form/CustomField";
import { Form, Formik } from "formik";
import { appendFormData, handleSubmitForm } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import { useSmartBack } from "hooks/useSmartBack";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  addPlan,
  getAddonsList,
  getPlanDetails,
  updatePlan,
} from "store/slices/membershipSlice";
import { BILLING_CYCLE, PLAN_TYPES } from "utils/constants";
import { formatOptionsForSelect } from "utils/formatOptions";
import { getMembershipPlanValidationSchema } from "utils/validations";

const AddEditPlan = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const id = useQueryParam("id");
  const isEdit = mode === "edit";
  const goBack = useSmartBack();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { canAction } = usePermission();
  const { canAdd, canEdit } = canAction("membership_plan");

  const { trash } = Icons || {};

  const { isLoading, loading, planDetails, addonsList } = useSelector(
    (state) => state.Membership,
  );

  const [initialValues, setInitialValues] = useState({
    planType: "unlimited",

    title: {
      en: "",
      ar: "",
    },

    subTitle: {
      en: "",
      ar: "",
    },

    description: {
      en: "",
      ar: "",
    },

    pricingOptions: [
      {
        label: "",
        billingCycle: "monthly", // monthly | yearly
        price: "",
        currency: "SAR",
        // durationInDays: 30,
        isUnlimited: true,
        isPopular: true,
      },
    ],

    addonEnabled: true,
    addonIncluded: false,

    addonsAllowed: [],

    features: [
      {
        title: {
          en: "",
          ar: "",
        },
      },
    ],

    isRecommended: true,
    sortOrder: 1,

    iconUrl: null,
  });

  useEffect(() => {
    dispatch(getAddonsList());
  }, []);

  useEffect(() => {
    if (isEdit && id && canEdit) {
      dispatch(getPlanDetails(id));
    }
  }, [isEdit, id, dispatch, canEdit]);

  useEffect(() => {
    if (isEdit && planDetails) {
      const {
        planType,
        title,
        subTitle,
        description,
        pricingOptions,
        addonEnabled,
        addonIncluded,
        addonsAllowed,
        features,
        isRecommended,
        sortOrder,
        iconUrl,
        highlightColor,
      } = planDetails;

      setInitialValues({
        planType,

        title: {
          en: title?.en || "",
          ar: title?.ar || "",
        },

        subTitle: {
          en: subTitle?.en || "",
          ar: subTitle?.ar || "",
        },

        description: {
          en: description?.en || "",
          ar: description?.ar || "",
        },
        highlightColor: highlightColor,

        pricingOptions:
          pricingOptions?.map(
            ({
              label,
              billingCycle,
              price,
              discountInPercentage,
              currency,
              isUnlimited,
              isPopular,
            }) => ({
              label: label || "",
              billingCycle: billingCycle || "",
              price: price ?? "",
              discountInPercentage: discountInPercentage ?? "",
              currency: currency || "SAR",
              isUnlimited: isUnlimited ?? true,
              isPopular: isPopular ?? false,
            }),
          ) || [],

        addonEnabled: addonEnabled ?? false,
        addonIncluded: addonIncluded ?? false,

        addonsAllowed: addonsAllowed?.map(({ _id }) => _id) || [],

        features: features?.map(({ title }) => ({
          title: {
            en: title?.en || "",
            ar: title?.ar || "",
          },
        })) || [{ title: { en: "", ar: "" } }],

        isRecommended: isRecommended ?? false,
        sortOrder: sortOrder ?? 1,

        iconUrl,
      });
    }
  }, [isEdit, planDetails]);

  const handleSubmit = (values) => {
    const {
      planType,
      title,
      subTitle,
      description,
      pricingOptions,
      addonEnabled,
      addonIncluded,
      addonsAllowed,
      features,
      isRecommended,
      sortOrder,
      highlightColor,
      iconUrl,
    } = values;

    const normalizedPricingOptions =
      planType === "unlimited"
        ? pricingOptions
        : pricingOptions.map(({ isPopular, ...rest }) => rest);

    const payload = {
      planType,
      title,
      subTitle,
      description,
      pricingOptions: normalizedPricingOptions,
      addonEnabled,
      addonIncluded,
      addonsAllowed,
      features,
      isRecommended,
      highlightColor,
      ...(isEdit && { sortOrder }),
      iconUrl,
    };
    const data = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      appendFormData(data, key, value);
    });

    if (isEdit) {
      data.append("id", id);

      handleSubmitForm({
        dispatch,
        action: updatePlan,
        payload: data,
        onSuccess: () => navigate(-1),
      });
    } else {
      handleSubmitForm({
        dispatch,
        action: addPlan,
        payload: data,
        onSuccess: () => navigate(-1),
      });
    }
  };

  const getAvailableBillingCycles = (pricingOptions, currentIndex) => {
    const selected = pricingOptions
      .filter((_, i) => i !== currentIndex)
      .map((p) => p.billingCycle);

    return BILLING_CYCLE.filter(
      (cycle) => cycle.value != "onetime" && !selected.includes(cycle.value),
    );
  };

  if (!canEdit && isEdit) return <NoPermission />;
  if (!canAdd && !isEdit) return <NoPermission />;

  if (isEdit && loading)
    return (
      <AddEditLoader title={t("Edit Membership Plan")} rows={7} cols={2} />
    );

  return (
    <div className="page-content add_plan">
      <PageHeader
        isBackBtn={true}
        title={isEdit ? t("Edit Membership Plan") : t("Add Membership Plan")}
      />

      <div className="common_card">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={getMembershipPlanValidationSchema(t, isEdit)}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form>
              <Row>
                <Col lg={12}>
                  <div className="common_tabs mb-3">
                    {PLAN_TYPES.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        className={`tabs_btn ${
                          values.planType === item.value ? "active" : ""
                        }`}
                        onClick={() => {
                          if (values.planType === item.value) return;

                          setFieldValue("planType", item.value);
                          if (item.value == "single_wash") {
                            setFieldValue("pricingOptions", [
                              {
                                label: "",
                                billingCycle: "onetime",
                                price: "",
                                currency: "SAR",
                                isUnlimited: item.value === "unlimited",
                                isPopular: item.value === "unlimited",
                              },
                            ]);
                          } else {
                            // reset pricing options on switch
                            setFieldValue("pricingOptions", [
                              {
                                label: "",
                                billingCycle: "",
                                price: "",
                                currency: "SAR",
                                isUnlimited: item.value === "unlimited",
                                isPopular: item.value === "unlimited",
                              },
                            ]);
                          }
                        }}
                      >
                        {t(item.name)}
                      </button>
                    ))}
                  </div>
                </Col>

                <h5 className="mb-3">{t("General Details")}</h5>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Title (English)")}
                      name="title.en"
                      placeholder={t("Enter Title (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="title.ar"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Title (Arabic)")}
                      name="title.ar"
                      placeholder={t("Enter Title (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="title.en"
                      required
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Highlight Title (English)")}
                      name="subTitle.en"
                      placeholder={t("Enter Highlight Title (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="subTitle.ar"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      label={t("Highlight Title (Arabic)")}
                      name="subTitle.ar"
                      placeholder={t("Enter Highlight Title (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="subTitle.en"
                      required
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      label={t("Description (English)")}
                      name="description.en"
                      placeholder={t("Enter Description (English)")}
                      enableTranslate
                      translateTo="ar"
                      targetField="description.ar"
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomField
                      as="textarea"
                      label={t("Description (Arabic)")}
                      name="description.ar"
                      placeholder={t("Enter Description (Arabic)")}
                      className="rtl"
                      enableTranslate
                      translateTo="en"
                      targetField="description.en"
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="form_field">
                    <CustomColorField
                      className="plan_color"
                      name="highlightColor"
                      label={t("Highlight Color")}
                    />
                  </div>
                </Col>

                {isEdit && (
                  <Col lg={6}>
                    <div className="form_field">
                      <CustomField
                        label={t("Sort Order")}
                        name="sortOrder"
                        type="number"
                        placeholder={t("Enter sort order")}
                      />
                    </div>
                  </Col>
                )}

                <hr className="mt-3" />
                <h5 className="mb-3">{t("Pricing Options")}</h5>
                {values?.pricingOptions?.map((item, index) => (
                  <Row key={index}>
                    <Col lg={3}>
                      <div className="form_field">
                        <CustomField
                          label={t("Label")}
                          required
                          name={`pricingOptions.${index}.label`}
                          placeholder={t("Enter label")}
                        />
                      </div>
                    </Col>

                    {values.planType == "unlimited" && (
                      <Col lg={3}>
                        <div className="form_field">
                          <SingleSelect
                            required
                            label={t("Billing Cycle")}
                            name={`pricingOptions.${index}.billingCycle`}
                            options={getAvailableBillingCycles(
                              values.pricingOptions,
                              index,
                            )}
                            value={item.billingCycle}
                            handleSingleSelectChange={(opt) =>
                              setFieldValue(
                                `pricingOptions.${index}.billingCycle`,
                                opt.value,
                              )
                            }
                          />
                        </div>
                      </Col>
                    )}

                    <Col lg={3}>
                      <div className="form_field">
                        <CustomField
                          required
                          label={t("Price")}
                          type="number"
                          name={`pricingOptions.${index}.price`}
                          placeholder="0"
                        />
                      </div>
                    </Col>
                    <Col lg={3}>
                      <div className="form_field">
                        <CustomField
                          label={t("Offer")}
                          type="number"
                          name={`pricingOptions.${index}.discountInPercentage`}
                          placeholder="0"
                        />
                      </div>
                    </Col>

                    {/* <Col>
                      <div className="form_field">
                        <Switch
                          label={t("Unlimited")}
                          checked={item.isUnlimited}
                          handleOnChange={() =>
                            setFieldValue(
                              `pricingOptions.${index}.isUnlimited`,
                              !item.isUnlimited
                            )
                          }
                        />
                      </div>
                    </Col> */}

                    <Col>
                      <div className="g_between">
                        {values.planType === "unlimited" && (
                          <div className="form_field">
                            <Switch
                              label={t("Popular")}
                              infoText={t(
                                "When enabled, this pricing option will be highlighted as the most popular option for customers.",
                              )}
                              checked={item.isPopular}
                              handleOnChange={() => {
                                const isCurrentlyPopular =
                                  values?.pricingOptions[index]?.isPopular;

                                const updated = values?.pricingOptions?.map(
                                  (opt, i) => ({
                                    ...opt,
                                    isPopular: isCurrentlyPopular
                                      ? false
                                      : i === index,
                                  }),
                                );

                                setFieldValue("pricingOptions", updated);
                              }}
                            />
                          </div>
                        )}
                        {values?.planType === "unlimited" &&
                          values?.pricingOptions?.length > 1 && (
                            <img
                              src={trash}
                              alt="trash"
                              width={20}
                              height={20}
                              className="c_p"
                              onClick={() => {
                                const updated = values?.pricingOptions?.filter(
                                  (_, i) => i !== index,
                                );
                                setFieldValue("pricingOptions", updated);
                              }}
                            />
                          )}
                      </div>
                    </Col>
                  </Row>
                ))}
                {values.planType === "unlimited" && (
                  <div className="g_end">
                    <button
                      type="button"
                      className="main_btn outline mt-2 w_fit_content"
                      onClick={() =>
                        setFieldValue("pricingOptions", [
                          ...values.pricingOptions,
                          {
                            label: "",
                            billingCycle: "",
                            price: "",
                            currency: "SAR",
                            isUnlimited: true,
                            isPopular: false,
                          },
                        ])
                      }
                      disabled={
                        values.pricingOptions.length >=
                        BILLING_CYCLE.filter(
                          (cycle) => cycle.value !== "onetime",
                        ).length
                      }
                    >
                      + {t("Add Pricing Option")}
                    </button>
                  </div>
                )}

                <hr className="my-3" />
                <h5>{t("Addons")}</h5>
                <Row>
                  <Col lg={4}>
                    {" "}
                    <div className="form_field">
                      <Switch
                        label={t("Addon Enabled")}
                        infoText={t(
                          "When enabled, customers can select add-ons for this plan. When disabled, no add-ons will be available.",
                        )}
                        checked={values.addonEnabled}
                        handleOnChange={() => {
                          const newValue = !values.addonEnabled;
                          setFieldValue("addonEnabled", newValue);
                          if (!newValue) {
                            setFieldValue("addonsAllowed", []);
                            setFieldValue("addonIncluded", false);
                          }
                        }}
                      />
                    </div>
                  </Col>
                  {values.addonEnabled && (
                    <>
                      <Col lg={4}>
                        <div className="form_field">
                          <Switch
                            label={t("Addon Included")}
                            infoText={t(
                              "When enabled, selected add-ons are included in the plan at no additional cost.",
                            )}
                            checked={values.addonIncluded}
                            handleOnChange={() =>
                              setFieldValue(
                                "addonIncluded",
                                !values.addonIncluded,
                              )
                            }
                          />
                        </div>
                      </Col>

                      <Col lg={4}>
                        <div className="form_field">
                          <MultiSelect
                            label={t("Allowed Addons")}
                            required
                            name="addonsAllowed"
                            value={values.addonsAllowed}
                            options={formatOptionsForSelect(
                              addonsList,
                              language,
                            )}
                            onChange={(val) =>
                              setFieldValue("addonsAllowed", val)
                            }
                          />
                        </div>
                      </Col>
                    </>
                  )}

                  <hr className="my-3" />
                  <h5>{t("Features")}</h5>

                  {values?.features?.map((_, index) => (
                    <Row key={index} className="align-items-end">
                      <Col lg={6}>
                        <div className="form_field">
                          <CustomField
                            label={t("Feature (English)")}
                            name={`features.${index}.title.en`}
                            placeholder={t("Enter Feature (English)")}
                            enableTranslate
                            translateTo="ar"
                            targetField={`features.${index}.title.ar`}
                            required
                          />
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="g_between gap-3 align-items-center">
                          <div className="form_field w-100">
                            <CustomField
                              label={t("Feature (Arabic)")}
                              name={`features.${index}.title.ar`}
                              placeholder={t("Enter Feature (Arabic)")}
                              className="rtl"
                              enableTranslate
                              translateTo="en"
                              targetField={`features.${index}.title.en`}
                              required
                            />
                          </div>
                          {values.features.length > 1 && (
                            <img
                              src={trash}
                              alt="trash"
                              width={20}
                              height={20}
                              className="c_p"
                              onClick={() => {
                                const updated = values.features.filter(
                                  (_, i) => i !== index,
                                );
                                setFieldValue("features", updated);
                              }}
                            />
                          )}
                        </div>
                      </Col>
                    </Row>
                  ))}
                  <div className="g_end">
                    <button
                      type="button"
                      className="main_btn outline mt-2"
                      onClick={() =>
                        setFieldValue("features", [
                          ...values.features,
                          { title: { en: "", ar: "" } },
                        ])
                      }
                    >
                      + {t("Add Feature")}
                    </button>
                  </div>
                </Row>

                <Col lg={6}>
                  <FileUpload
                    uploadedFiles={values.iconUrl || null}
                    label={t("Upload Icon")}
                    setUploadedFiles={(file) => setFieldValue("iconUrl", file)}
                    multiple={false}
                    maxFiles={1}
                    maxFileSize={0.1 * 1024 * 1024}
                    acceptedFileTypes={["image/png", "image/jpeg"]}
                    className={
                      touched?.iconUrl && errors.iconUrl ? "invalid" : ""
                    }
                  />
                </Col>

                <Col lg={12} className="d-flex justify-content-end gap-2 mt-5">
                  <button
                    type="button"
                    className="main_btn outline"
                    onClick={goBack}
                  >
                    {t("Cancel")}
                  </button>

                  <button
                    type="submit"
                    className="main_btn"
                    disabled={isLoading}
                  >
                    {isEdit ? t("Save Changes") : t("Add Plan")}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddEditPlan;
